import { ChevronRightIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { motion } from 'framer-motion'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { PaymentData, PaymentDataAtom, paymentsMethodModalAtom, switchedPaymentMethodAtom } from '@/state/payment'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import {
  paymentMethodsAtom,
  userInfoAtom,
  venueAtom,
  verifyComponentAtom,
  payWithApplePayAtom,
  payWithGooglePayAtom,
  tryOpeningHostedPayentPageAtom,
  canPayWithApplePayAtom,
  canPayWithGooglePayAtom,
} from '@/state/global'
import { ANIMATION_VARIANTS, CC_ENTRY_MODES, PaymentMethodInputTypes } from '@/utils/constants'
import HostedPage from './HostedPage'
import MagensaUnigateHostedPage from './MagensaUnigateHostedPage'
import { useApplePay } from '@/hooks/useApplePay'
import { useGooglePay } from '@/hooks/useGooglePay'
import { useMagensaGooglePay } from '@/hooks/useMagensaGooglePay'
import { useMagensaApplePay } from '@/hooks/useMagensaApplePay'
import { addPaymentMethodClicked, paymentMethodClicked } from '@/utils/amplitudeApis'
import { getCardIcon } from '@/utils/magensa'

function maskCard(pan_mask: string): string {
  const last4 = pan_mask.slice(-4)
  return `**** ${last4}`
}

let _hostedPageOwner: string | null = null

function PaymentMethods({
  show = true,
  handleNextAction,
  hasRunningTab = false,
}: {
  show?: boolean
  handleNextAction?: (args: any) => void
  hasRunningTab?: boolean
}) {
  const instanceId = useRef(Math.random().toString(36).slice(2, 8))
  const [tryOpeningHostedPayentPage, setTryOpeningHostedPaymentPage] = useAtom(tryOpeningHostedPayentPageAtom)

  const { isApplePaySupported } = useApplePay()
  const { isGooglePaySupported } = useGooglePay()
  const { isMagensaGooglePaySupported } = useMagensaGooglePay()
  const { isMagensaApplePaySupported } = useMagensaApplePay()
  const [isOpen, setOpen] = useAtom(paymentsMethodModalAtom)
  const [hostedPageIsOpen, setHostedPageIsOpen] = useState(false)
  const [magensaUnigateHostedPageIsOpen, setMagensaUnigateHostedPageIsOpen] = useState(false)
  const [paymentMethods, setPaymentMethods] = useAtom(paymentMethodsAtom)
  const canPayWithApplePay = useAtomValue(canPayWithApplePayAtom)
  const canPayWithGooglePay = useAtomValue(canPayWithGooglePayAtom)
  const [payWithApplePay, setPayWithApplePay] = useAtom(payWithApplePayAtom)
  const [payWithGooglePay, setPayWithGooglePay] = useAtom(payWithGooglePayAtom)
  const user = useAtomValue(userInfoAtom)
  const [paymentMethod, setPaymentMethod] = useAtom(PaymentDataAtom)
  const venue = useAtomValue(venueAtom)
  const [verifyComponentIsOpen, setVerifyComponentIsOpen] = useAtom(verifyComponentAtom)
  const setDidSwitchPaymentMethod = useSetAtom(switchedPaymentMethodAtom)

  useEffect(() => {
    if (!verifyComponentIsOpen && tryOpeningHostedPayentPage && _hostedPageOwner === instanceId.current) {
      setTryOpeningHostedPaymentPage(false)
      if (user) {
        setOpen(false)
        if (venue?.cc_entry_mode === 'magensa_unigate_hosted_page') {
          setMagensaUnigateHostedPageIsOpen(true)
        } else {
          setHostedPageIsOpen(true)
        }
      }
    }
  }, [verifyComponentIsOpen, tryOpeningHostedPayentPage, venue?.cc_entry_mode])

  const handleClose = () => {
    setOpen(false)
  }

  const handleSelectPayment = (method: PaymentData) => {
    setPaymentMethod(method)
    if (hasRunningTab) {
      setDidSwitchPaymentMethod(true)
    }
  }

  const handleDeletePaymentMethod = (e: React.MouseEvent<SVGSVGElement, MouseEvent>, method: PaymentData) => {
    e.stopPropagation()
    const newList = paymentMethods.filter((item) => item.pan_mask !== method.pan_mask)
    localStorage.setItem('paymentMethods', JSON.stringify(newList))
    setPaymentMethods(newList)
    if (newList.length > 0) {
      setPaymentMethod(newList[0])
    } else {
      setPaymentMethod(null)
      if (canPayWithApplePay) {
        setPayWithApplePay(true)
      } else if (canPayWithGooglePay) {
        setPayWithGooglePay(true)
      }
    }

    if (hasRunningTab) {
      setDidSwitchPaymentMethod(true)
    }
  }

  const renderPaymentMethod = (method: PaymentData) => {
    switch (method.input_mode) {
      case 'fortis_ecommerce_manual_keyed':
        if (venue?.cc_entry_mode !== 'fortis_hosted_page') return null

        return (
          <button
            className="bg-black border-b py-5 border-gray-800 w-full flex justify-between"
            key={method.pan_mask}
            onClick={() => {
              handleSelectPayment(method)
              setPayWithApplePay(false)
              setPayWithGooglePay(false)
              handleClose()
            }}
          >
            <div className="flex gap-5">
              <img src={`/Payment/${getCardIcon(method.network)}.svg`} />
              <span className="whitespace-nowrap">{maskCard(method.pan_mask)}</span>
            </div>
            <TrashIcon className="w-6 text-primary-red" onClick={(e) => handleDeletePaymentMethod(e, method)} />
          </button>
        )
      case 'magensa_unigate_ecommerce_manual_keyed':
        if (venue?.cc_entry_mode !== 'magensa_unigate_hosted_page') return null

        return (
          <button
            className="bg-black border-b py-5 border-gray-800 w-full flex justify-between"
            key={method.pan_mask}
            onClick={() => {
              handleSelectPayment(method)
              setPayWithApplePay(false)
              setPayWithGooglePay(false)
              handleClose()
            }}
          >
            <div className="flex gap-5">
              <img src={`/Payment/${getCardIcon(method.network)}.svg`} />
              <span className="whitespace-nowrap">{maskCard(method.pan_mask)}</span>
            </div>
            <TrashIcon className="w-6 text-primary-red" onClick={(e) => handleDeletePaymentMethod(e, method)} />
          </button>
        )
      default:
        return null
    }
  }

  const paymentMethodSelected = () => {
    if (!paymentMethod && !payWithApplePay && !payWithGooglePay) {
      return null
    }

    if (payWithApplePay) {
      return (
        <div className="flex gap-2 items-center">
          <img src={`/Payment/fortis_apple_pay_web.svg`} className="w-9" />
        </div>
      )
    } else if (payWithGooglePay) {
      return (
        <div className="flex gap-2 items-center">
          <img src={`/Payment/fortis_google_pay_web.svg`} className="w-9" />
        </div>
      )
    } else if (paymentMethod) {
      if (
        paymentMethod.input_mode === PaymentMethodInputTypes.FORTIS_APPLE_PAY_WEB ||
        paymentMethod.input_mode === PaymentMethodInputTypes.MAGENSA_UNIGATE_APPLE_PAY_WEB
      ) {
        setPayWithApplePay(true)
        return (
          <div className="flex gap-2 items-center">
            <img src={`/Payment/fortis_apple_pay_web.svg`} className="w-9" />
          </div>
        )
      } else if (
        paymentMethod.input_mode === PaymentMethodInputTypes.FORTIS_GOOGLE_PAY_WEB ||
        paymentMethod.input_mode === PaymentMethodInputTypes.MAGENSA_UNIGATE_GOOGLE_PAY_WEB
      ) {
        setPayWithGooglePay(true)
        return (
          <div className="flex gap-2 items-center">
            <img src={`/Payment/fortis_google_pay_web.svg`} className="w-9" />
          </div>
        )
      }
      return (
        <div className="flex gap-2 items-center">
          <span>{maskCard(paymentMethod?.pan_mask ?? '')}</span>
          <img src={`/Payment/${getCardIcon(paymentMethod?.network)}.svg`} className="w-9" />
        </div>
      )
    }
  }

  const closePage = (paymentMethod: PaymentData | undefined) => {
    _hostedPageOwner = null
    setOpen(false)
    setHostedPageIsOpen(false)
    setMagensaUnigateHostedPageIsOpen(false)
    if (paymentMethod) {
      handleNextAction?.(paymentMethod)
    }
  }

  const handleApplePay = () => {
    if (!isApplePaySupported && !isMagensaApplePaySupported) {
      return null
    }
    return (
      <div
        className="bg-black border-b py-2 border-gray-800 w-full flex justify-start"
        onClick={() => {
          setPayWithApplePay(true)
          setPayWithGooglePay(false)
          setPaymentMethod(null)
          handleClose()
        }}
      >
        <div className="flex items-center gap-3 justify-start">
          <img src={`/Payment/fortis_apple_pay_web.svg`} className="w-10" />
          <span>Apple Pay</span>
        </div>
      </div>
    )
  }

  const handleGooglePay = () => {
    if (!isGooglePaySupported && !isMagensaGooglePaySupported) {
      return null
    }
    return (
      <div
        className="bg-black border-b py-2 border-gray-800 w-full flex justify-start"
        onClick={() => {
          setPayWithApplePay(false)
          setPayWithGooglePay(true)
          setPaymentMethod(null)
          handleClose()
        }}
      >
        <div className="flex items-center gap-3 justify-start">
          <img src={`/Payment/fortis_google_pay_web.svg`} className="w-10" />
          <span>Google Pay</span>
        </div>
      </div>
    )
  }

  const handleAddPaymentMethod = () => {
    _hostedPageOwner = instanceId.current
    addPaymentMethodClicked()
    if (!user) {
      setOpen(false)
      setVerifyComponentIsOpen(true)
      setTryOpeningHostedPaymentPage(true)
      return
    }

    setTryOpeningHostedPaymentPage(false)
    setPayWithApplePay(false)
    setPayWithGooglePay(false)
    setOpen(false)

    if (venue?.cc_entry_mode === 'magensa_unigate_hosted_page') {
      setMagensaUnigateHostedPageIsOpen(true)
    } else {
      setHostedPageIsOpen(true)
    }
  }

  return (
    <>
      {!isOpen && show && (
        <button
          className="flex justify-between items-center layout pt-3 pb-3 bg-black"
          onClick={() => {
            paymentMethodClicked()
            setOpen(!isOpen)
          }}
        >
          <span>Payment Method</span>
          <div className="flex items-center">
            {paymentMethodSelected()}
            <ChevronRightIcon className="w-6" />
          </div>
        </button>
      )}

      {isOpen && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-white bg-opacity-80" aria-hidden />
          <motion.div
            className="w-full absolute bottom-2 layout flex justify-center"
            animate="animate"
            variants={ANIMATION_VARIANTS}
            initial="initial"
            exit="animate"
          >
            <div className="bg-black text-white w-11/12 rounded-2xl pt-2 relative">
              <div className="absolute flex top-0 w-full justify-center items-center p-3 text-md">
                <span>PAYMENT METHODS</span>
              </div>

              <button type="button" className="absolute right-3 z-10 cursor-pointer" onClick={handleClose} aria-label="Close">
                <XMarkIcon className="w-7" />
              </button>
              <div className="h-60 overflow-y-auto mt-10 px-8">
                {handleGooglePay()}
                {handleApplePay()}
                {paymentMethods?.map(renderPaymentMethod)}
              </div>

              {CC_ENTRY_MODES.includes(venue?.cc_entry_mode) && (
                <div
                  className="border-t border-gray-600 border-opacity-70 text-gray-200 flex items-center justify-center py-5"
                  onClick={handleAddPaymentMethod}
                >
                  Add Payment Method
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {hostedPageIsOpen ? <HostedPage closePage={closePage} hasRunningTab={hasRunningTab} /> : null}
      {magensaUnigateHostedPageIsOpen ? <MagensaUnigateHostedPage closePage={closePage} hasRunningTab={hasRunningTab} /> : null}
    </>
  )
}

export default PaymentMethods
